import { getStatuses } from '../../lib/statuses'
import { Key } from './Key'
import { useEffect, useState } from 'react'
import { ENTER_TEXT, DELETE_TEXT, ARROW_RIGHT, ARROW_LEFT } from '../../constants/strings'
import { localeAwareUpperCase } from '../../lib/words'
import { Ping } from '../../components/alerts/Ping'
import { getStoredHasSeenHint, setStoredHasSeenHint } from '../../lib/localStorage'
import { LightBulbIcon } from '@heroicons/react/outline'
import {
  GameMode,
  GAME_MODE_DAILY,
  GAME_MODE_MINI_DAILY,
  GAME_MODE_UNLIMITED,
  GAME_MODE_CUSTOM
} from '../../constants/strings'

type Props = {
  onArrow: (key: string) => void
  onChar: (value: string) => void
  onDelete: () => void
  onEnter: () => void
  onShift: (pressed: boolean) => void 
  onHint: () => void
  solution: string
  guesses: string[]
  isRevealing?: boolean
  revealTime: number
  gameMode: GameMode
  enableHints: boolean
  isGameOver: boolean
}

export const Keyboard = ({
  onArrow,
  onChar,
  onDelete,
  onEnter,
  onShift,
  onHint,
  solution,
  guesses,
  isRevealing,
  revealTime,
  gameMode,
  enableHints,
  isGameOver
}: Props) => {

  const [pingHint, setPingHint] = useState(false)
  const charStatuses = getStatuses(solution, guesses)

  const onHintWrapper = () => {
    setStoredHasSeenHint(true)
    setPingHint(false)
    onHint()
  }

  const onClick = (value: string) => {
    // remove focus from menu button if selected
    if (value === 'ENTER') {
      onEnter()
    } else if (value === 'DELETE') {
      onDelete()
    } else if (value === 'HINT') {
      onHintWrapper()
    } else {
      onChar(value)
    }
  }

  useEffect(() => {
    if(!enableHints || getStoredHasSeenHint()) {
      return
    }

    setPingHint(false)
    const tick = setInterval(() => { 
      setPingHint(true)
    }, 60*1000);
    return () => {
      clearInterval(tick);
    }
  }, [enableHints, guesses])

  useEffect(() => {
    // TODO only listen if there are no modals
    const listener = (e: KeyboardEvent) => {
      const activeElement = document.activeElement
      const isFocused = activeElement === document.body || document.getElementById('root')!.contains(activeElement)
      if(!isFocused) {
        return
      }
      const isEnter = e.key === 'Enter' || e.keyCode === 13;
      const isBackspace = e.code === 'Backspace';
      const isLeftArrow = e.code === ARROW_LEFT;
      const isRightArrow = e.code === ARROW_RIGHT;
      const key = localeAwareUpperCase(e.key);
      const isKeypress = key.length === 1 && ((key >= 'A' && key <= 'Z') || key === ' ');
      const isSpace = e.key === ' ';
      if(isSpace || isBackspace || isLeftArrow || isRightArrow) {
        e.preventDefault(); // disable page down && firefox back issue
      }
      onShift(e.shiftKey)
      if(isRightArrow || isLeftArrow) {
        onArrow(e.code)
      } else if (isEnter) {
        onEnter()
      } else if (isBackspace) {
        onDelete()
      } else if (isKeypress){
        onChar(key)
      }
    }
    window.addEventListener('keydown', listener)
    return () => {
      window.removeEventListener('keydown', listener)
    }
  }, [onArrow, onEnter, onDelete, onChar, onShift])

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      const activeElement = document.activeElement
      const isFocused = activeElement === document.body || document.getElementById('root')!.contains(activeElement)
      if(!isFocused) {
        return
      }
      onShift(e.shiftKey)
    }
    window.addEventListener('keyup', listener)
    return () => {
      window.removeEventListener('keyup', listener)
    }
  }, [onShift])

  return (
    <div className="w-full" aria-label="keyboard" role="list">
      <div className="flex justify-center mb-1">
        {['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'].map((key) => (
          <Key
            value={key}
            key={key}
            onClick={onClick}
            status={charStatuses[key]}
            isRevealing={isRevealing}
            solution={solution}
            revealTime={revealTime}
          />
        ))}
      </div>
      <div className="flex justify-center mb-1">
        {enableHints && new Set([
          GAME_MODE_UNLIMITED,
          GAME_MODE_DAILY,
          GAME_MODE_CUSTOM,
          GAME_MODE_MINI_DAILY]).has(gameMode) &&
        <div className="flex items-center justify-center rounded bg-indigo-900 mx-0.5 relative" style={{width: 40}}>
          <Key margin={false} width={20} value="HINT" status="action" onClick={onClick} solution={solution} revealTime={revealTime}>
            <LightBulbIcon
              id="nav-btn-hint"
              className="h-6 w-6 cursor-pointer dark:stroke-white"
              aria-label="hint"
              role="button"
            />
          </Key>
					<Ping isOn={pingHint && !isGameOver} />
        </div>
        }
        {['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'].map((key) => (
          <Key
            value={key}
            key={key}
            onClick={onClick}
            status={charStatuses[key]}
            isRevealing={isRevealing}
            solution={solution}
            revealTime={revealTime}
          />
        ))}
      </div>
      <div className="flex justify-center">
        <Key width={65.4} value="ENTER" onClick={onClick} solution={solution} revealTime={revealTime}>
          {ENTER_TEXT}
        </Key>
        {['Z', 'X', 'C', 'V', 'B', 'N', 'M'].map((key) => (
          <Key
            value={key}
            key={key}
            onClick={onClick}
            status={charStatuses[key]}
            isRevealing={isRevealing}
            solution={solution}
            revealTime={revealTime}
          />
        ))}
        <Key width={65.4} value="DELETE" onClick={onClick} solution={solution} revealTime={revealTime}>
          {DELETE_TEXT}
        </Key>
      </div>
    </div>
  )
}
